
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserProvider from 'Models/Security/Acl/UserProvider';
import VisitorsProvider from 'Models/Security/Acl/VisitorsProvider';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeFetchManyToManyFunc,
	makeFetchOneToManyFunc,
	makeJoinEqualsFunc,
} from 'Util/EntityUtils';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

export interface IProviderEntityAttributes extends IModelAttributes {
	name: string;
	description: string;
	isActive: boolean;
	providerProducts: Array<
		| Models.ProviderProductEntity
		| Models.IProviderProductEntityAttributes
	>;
	organisations: Array<
		| Models.OrganisationProvider
		| Models.IOrganisationProviderAttributes
	>;
}

@entity('ProviderEntity', 'Provider')
export default class ProviderEntity extends Model
	implements IProviderEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserProvider(),
		new VisitorsProvider(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ProviderEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	@observable
	@attribute<ProviderEntity, string>()
	@CRUD({
		name: 'Description',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public description: string;

	@observable
	@attribute<ProviderEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'Provider Products',
		displayType: 'reference-multicombobox',
		order: 80,
		referenceTypeFunc: () => Models.ProviderProductEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'providerProducts',
			oppositeEntity: () => Models.ProviderProductEntity,
		}),

	})
	public providerProducts: Models.ProviderProductEntity[] = [];

	/**
	 * Providers the organisation has access to
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'Organisation',
		displayType: 'reference-multicombobox',
		order: 90,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.OrganisationProvider,
		optionEqualFunc: makeJoinEqualsFunc('organisationId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'providerEntity',
			oppositeEntityName: 'organisationEntity',
			relationName: 'provider',
			relationOppositeName: 'organisation',
			entity: () => Models.ProviderEntity,
			joinEntity: () => Models.OrganisationProvider,
			oppositeEntity: () => Models.OrganisationEntity,
		}),

	})
	public organisations: Models.OrganisationProvider[] = [];

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IProviderEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IProviderEntityAttributes>) {

		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.providerProducts !== undefined && Array.isArray(attributes.providerProducts)) {
				for (const model of attributes.providerProducts) {
					if (model instanceof Models.ProviderProductEntity) {
						this.providerProducts.push(model);
					} else {
						this.providerProducts.push(new Models.ProviderProductEntity(model));
					}
				}
			}
			if (attributes.organisations !== undefined && Array.isArray(attributes.organisations)) {
				for (const model of attributes.organisations) {
					if (model instanceof Models.OrganisationProvider) {
						this.organisations.push(model);
					} else {
						this.organisations.push(new Models.OrganisationProvider(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */

	public defaultExpands = `
		organisations {
			${Models.OrganisationProvider.getAttributes().join('\n')}
			organisation {
				${Models.OrganisationEntity.getAttributes().join('\n')}
				${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
			}
		}
		providerProducts {
			${Models.ProviderProductEntity.getAttributes().join('\n')}
		}
	`;
	/**
	 * The save method that is called from the admin CRUD components.
	 */

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			organisations: {},
			providerProducts: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'organisations',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ProviderEntity.prototype, 'created');
CRUD(modifiedAttr)(ProviderEntity.prototype, 'modified');
